/**
 * 本地存储工具
 * 用于管理认证相关数据的存储
 */

import type { UserInfo } from './types'

/**
 * 存储键名常量
 */
export const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  USER: 'auth_user',
  PERMISSIONS: 'auth_permissions',
  ROLES: 'auth_roles',
  TOKEN_EXPIRATION: 'auth_token_expiration'
} as const

/**
 * 存储类型
 */
export type StorageType = 'localStorage' | 'sessionStorage'

/**
 * 存储配置
 */
export interface StorageConfig {
  type: StorageType
  prefix?: string
}

/**
 * 存储工具类
 */
class Storage {
  private storageType: StorageType = 'localStorage'
  private prefix: string = ''

  /**
   * 配置存储
   */
  configure(config: StorageConfig): void {
    this.storageType = config.type
    this.prefix = config.prefix || ''
  }

  /**
   * 获取存储实例
   */
  private getStorage(): globalThis.Storage {
    return this.storageType === 'localStorage' ? localStorage : sessionStorage
  }

  /**
   * 生成完整的存储键名
   */
  private getKey(key: string): string {
    return this.prefix ? `${this.prefix}_${key}` : key
  }

  /**
   * 设置数据
   */
  setItem(key: string, value: any): void {
    try {
      const storage = this.getStorage()
      const serializedValue = JSON.stringify(value)
      storage.setItem(this.getKey(key), serializedValue)
    } catch (error) {
      console.error('存储数据失败:', error)
    }
  }

  /**
   * 获取数据
   */
  getItem<T = any>(key: string): T | null {
    try {
      const storage = this.getStorage()
      const value = storage.getItem(this.getKey(key))
      return value ? JSON.parse(value) : null
    } catch (error) {
      console.error('读取数据失败:', error)
      return null
    }
  }

  /**
   * 删除数据
   */
  removeItem(key: string): void {
    try {
      const storage = this.getStorage()
      storage.removeItem(this.getKey(key))
    } catch (error) {
      console.error('删除数据失败:', error)
    }
  }

  /**
   * 清空所有数据
   */
  clear(): void {
    try {
      const storage = this.getStorage()
      if (this.prefix) {
        // 只清除带前缀的数据
        const keys = Object.keys(storage)
        keys.forEach(key => {
          if (key.startsWith(this.prefix)) {
            storage.removeItem(key)
          }
        })
      } else {
        storage.clear()
      }
    } catch (error) {
      console.error('清空数据失败:', error)
    }
  }

  // 认证相关的便捷方法

  /**
   * 设置Token
   */
  setToken(token: string): void {
    this.setItem(STORAGE_KEYS.TOKEN, token)
  }

  /**
   * 获取Token
   */
  getToken(): string | null {
    return this.getItem<string>(STORAGE_KEYS.TOKEN)
  }

  /**
   * 删除Token
   */
  removeToken(): void {
    this.removeItem(STORAGE_KEYS.TOKEN)
  }

  /**
   * 设置刷新Token
   */
  setRefreshToken(token: string): void {
    this.setItem(STORAGE_KEYS.REFRESH_TOKEN, token)
  }

  /**
   * 获取刷新Token
   */
  getRefreshToken(): string | null {
    return this.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN)
  }

  /**
   * 删除刷新Token
   */
  removeRefreshToken(): void {
    this.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  }

  /**
   * 设置用户信息
   */
  setUser(user: UserInfo): void {
    this.setItem(STORAGE_KEYS.USER, user)
  }

  /**
   * 获取用户信息
   */
  getUser(): UserInfo | null {
    return this.getItem<UserInfo>(STORAGE_KEYS.USER)
  }

  /**
   * 删除用户信息
   */
  removeUser(): void {
    this.removeItem(STORAGE_KEYS.USER)
  }

  /**
   * 设置权限列表
   */
  setPermissions(permissions: string[]): void {
    this.setItem(STORAGE_KEYS.PERMISSIONS, permissions)
  }

  /**
   * 获取权限列表
   */
  getPermissions(): string[] | null {
    return this.getItem<string[]>(STORAGE_KEYS.PERMISSIONS)
  }

  /**
   * 删除权限列表
   */
  removePermissions(): void {
    this.removeItem(STORAGE_KEYS.PERMISSIONS)
  }

  /**
   * 设置角色列表
   */
  setRoles(roles: string[]): void {
    this.setItem(STORAGE_KEYS.ROLES, roles)
  }

  /**
   * 获取角色列表
   */
  getRoles(): string[] | null {
    return this.getItem<string[]>(STORAGE_KEYS.ROLES)
  }

  /**
   * 删除角色列表
   */
  removeRoles(): void {
    this.removeItem(STORAGE_KEYS.ROLES)
  }

  /**
   * 设置Token过期时间
   */
  setTokenExpiration(expiration: string): void {
    this.setItem(STORAGE_KEYS.TOKEN_EXPIRATION, expiration)
  }

  /**
   * 获取Token过期时间
   */
  getTokenExpiration(): string | null {
    return this.getItem<string>(STORAGE_KEYS.TOKEN_EXPIRATION)
  }

  /**
   * 删除Token过期时间
   */
  removeTokenExpiration(): void {
    this.removeItem(STORAGE_KEYS.TOKEN_EXPIRATION)
  }

  /**
   * 清除所有认证相关数据
   */
  clearAuth(): void {
    this.removeToken()
    this.removeRefreshToken()
    this.removeUser()
    this.removePermissions()
    this.removeRoles()
    this.removeTokenExpiration()
  }
}

// 创建全局存储实例
export const storage = new Storage()

// 默认配置
storage.configure({
  type: 'localStorage',
  prefix: 'duohe_admin'
})
